import { useMemo } from 'react';

import {
  SmartLkLibraryStreamQuerySort,
  useLibraryCollectionWithPaginationLazyQuery,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { NetworkStatus } from '@apollo/client';

const INITIAL_PAGINATION = {
  page: 0,
  pageSize: 20,
};

const useGetCollection = () => {
  const [getCollection, collectionData] = useLibraryCollectionWithPaginationLazyQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    notifyOnNetworkStatusChange: true,
  });
  const collectionDataStreams = collectionData.data?.result.result;
  const collectionDataPagination = collectionData.data?.result.paginationInfo;
  const isFetching = collectionData.networkStatus === NetworkStatus.fetchMore;

  const request = (collectionId: string, companyId: string) => {
    getCollection({
      variables: {
        filters: { collectionId, companyId },
        pagination: INITIAL_PAGINATION,
        sort: SmartLkLibraryStreamQuerySort.IdDesc,
      },
    });
  };

  const loadMore = (collectionId: string, companyId: string) => {
    collectionData.fetchMore({
      variables: {
        filters: { collectionId, companyId },
        pagination: {
          page: (collectionDataPagination?.page ?? 0) + 1,
          pageSize: 20,
        },
        sort: SmartLkLibraryStreamQuerySort.IdDesc,
      },
      updateQuery: (prev, { fetchMoreResult }) => ({
        ...prev,
        result: {
          paginationInfo: {
            ...prev.result.paginationInfo,
            ...fetchMoreResult.result.paginationInfo,
          },
          result: [...(prev.result.result || []), ...(fetchMoreResult.result.result || [])],
        },
      }),
    });
  };

  return useMemo(
    () => ({
      request,
      loadMore,
      collectionDataStreams,
      collectionDataPagination,
      loading: collectionData.loading,
      isFetching,
    }),
    [collectionData]
  );
};

export default useGetCollection;
